// src/components/modules/crm/AddPersonaForm.tsx
import React, { useState, useEffect } from 'react';
import { Card, Form, Row, Col, Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

interface ProcesoOption {
  proc: string;
  AUX7: string;
}

interface RolOption {
  id: number;
  nombre: string;
}

const AddPersonaForm = () => {
  const navigate = useNavigate();
  const [procesos, setProcesos] = useState<ProcesoOption[]>([]);
  const [roles, setRoles] = useState<RolOption[]>([]);
  const [loadingDeps, setLoadingDeps] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:2000';


  // Estados del formulario
  const [formData, setFormData] = useState({
    id: '', nombres: '', apellido: '', direccion: '', fecha_de_nacimiento: '',
    cuil: '', persona_de_confianza_1: '', persona_de_confianza_2: '', email: '',
    cliente: 'No', celular: '', proceso_proc: '', rol_id: ''
  });

  const [files, setFiles] = useState<{ dni_frente: File | null; dni_dorso: File | null }>({
    dni_frente: null,
    dni_dorso: null,
  });

useEffect(() => {
    // Fetch para traer Procesos y Roles
    const fetchDependencies = async () => {
      try {
        const response = await fetch(`${apiUrl}/personas/dependenciasformulario`);
        if (!response.ok) throw new Error('Error en la respuesta de red');
        
        const data = await response.json();
        // Agregamos || [] para que nunca sea undefined
        setProcesos(data.procesos || []);
        setRoles(data.roles || []);
      } catch (error) {
        console.error("Error cargando dependencias", error);
        // Si hay error, nos aseguramos de que queden como arreglos vacíos
        setProcesos([]);
        setRoles([]);
      } finally {
        setLoadingDeps(false);
      }
    };
    fetchDependencies();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files: selectedFiles } = e.target;
    if (selectedFiles && selectedFiles.length > 0) {
      setFiles((prev) => ({ ...prev, [name]: selectedFiles[0] }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Usamos FormData porque hay archivos (blobs)
    const submitData = new FormData();
    
    Object.entries(formData).forEach(([key, value]) => {
      // split y limpieza si el campo es celular
      if (key === 'celular' && value) {
        // Elimina todos los espacios (\s) y guiones (-)
        const celularLimpio ="549" + value.replace(/\s|-/g, '');
        submitData.append(key, celularLimpio);
      } else {
        submitData.append(key, value);
      }
    });

    if (files.dni_frente) submitData.append('dni_frente', files.dni_frente);
    if (files.dni_dorso) submitData.append('dni_dorso', files.dni_dorso);

    try {
      const response = await fetch(`${apiUrl}/personas/create`, {
        method: 'POST',
        body: submitData,
        // No se pone 'Content-Type' manualmente cuando se usa FormData
      });

      if (!response.ok) throw new Error('Error al guardar');
      
      alert('Persona agregada exitosamente');
      navigate('/app/apps/crm/personas'); // Ajusta la ruta a tu listado principal
    } catch (error) {
      alert('Hubo un error al crear la persona');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingDeps) return <Spinner animation="border" />;

  return (
    <Card className="mb-3">
      <Card.Body>
        <h4 className="mb-4">Agregar Nueva Persona</h4>
        <Form onSubmit={handleSubmit}>
          
          <h5 className="mb-3 text-body-tertiary">Datos Personales</h5>
          <Row className="mb-3 g-3">
            <Form.Group as={Col} md="4">
              <Form.Label>DNI <span className="text-danger">*</span></Form.Label>
              <Form.Control required type="text" name="id" value={formData.id} onChange={handleChange} placeholder="Ej: 29006501" />
            </Form.Group>
            <Form.Group as={Col} md="4">
              <Form.Label>Nombres</Form.Label>
              <Form.Control type="text" name="nombres" value={formData.nombres} onChange={handleChange} />
            </Form.Group>
            <Form.Group as={Col} md="4">
              <Form.Label>Apellido</Form.Label>
              <Form.Control type="text" name="apellido" value={formData.apellido} onChange={handleChange} />
            </Form.Group>
          </Row>

          <Row className="mb-3 g-3">
            <Form.Group as={Col} md="4">
              <Form.Label>CUIL</Form.Label>
              <Form.Control type="text" name="cuil" value={formData.cuil} onChange={handleChange} />
            </Form.Group>
            <Form.Group as={Col} md="4">
              <Form.Label>Fecha de Nacimiento</Form.Label>
              <Form.Control type="date" name="fecha_de_nacimiento" value={formData.fecha_de_nacimiento} onChange={handleChange} />
            </Form.Group>
            <Form.Group as={Col} md="4">
              <Form.Label>Cliente</Form.Label>
              <Form.Select name="cliente" value={formData.cliente} onChange={handleChange}>
                <option value="No">No</option>
                <option value="Si">Sí</option>
              </Form.Select>
            </Form.Group>
          </Row>

          <hr className="my-4" />
          <h5 className="mb-3 text-body-tertiary">Contacto y Vínculos</h5>
          
          <Row className="mb-3 g-3">
            <Form.Group as={Col} md="4">
              <Form.Label>Celular (Código de área + número (sin 549))</Form.Label>
              <Form.Control type="text" name="celular" value={formData.celular} onChange={handleChange} placeholder="Ej: 1151850154" />
              <Form.Text className="text-muted">Código de área + número (sin 549)</Form.Text>
            </Form.Group>
            <Form.Group as={Col} md="4">
              <Form.Label>Email</Form.Label>
              <Form.Control type="email" name="email" value={formData.email} onChange={handleChange} />
            </Form.Group>
            <Form.Group as={Col} md="4">
              <Form.Label>Dirección</Form.Label>
              <Form.Control type="text" name="direccion" value={formData.direccion} onChange={handleChange} />
            </Form.Group>
          </Row>

          <Row className="mb-3 g-3">
            <Form.Group as={Col} md="6">
              <Form.Label>DNI Persona de Confianza 1</Form.Label>
              <Form.Control type="text" name="persona_de_confianza_1" value={formData.persona_de_confianza_1} onChange={handleChange} placeholder="Ingrese DNI" />
            </Form.Group>
            <Form.Group as={Col} md="6">
              <Form.Label>DNI Persona de Confianza 2</Form.Label>
              <Form.Control type="text" name="persona_de_confianza_2" value={formData.persona_de_confianza_2} onChange={handleChange} placeholder="Ingrese DNI" />
            </Form.Group>
          </Row>

          <hr className="my-4" />
          <h5 className="mb-3 text-body-tertiary">Documentación</h5>

          <Row className="mb-3 g-3">
            <Form.Group as={Col} md="6">
              <Form.Label>DNI Frente</Form.Label>
              <Form.Control type="file" name="dni_frente" onChange={handleFileChange} />
            </Form.Group>
            <Form.Group as={Col} md="6">
              <Form.Label>DNI Dorso</Form.Label>
              <Form.Control type="file" name="dni_dorso" onChange={handleFileChange} />
            </Form.Group>
          </Row>

          <hr className="my-4" />
          <h5 className="mb-3 text-body-tertiary">Vincular a Proceso (Opcional)</h5>

          <Row className="mb-4 g-3">
            <Form.Group as={Col} md="6">
              <Form.Label>Proceso</Form.Label>
              <Form.Select name="proceso_proc" value={formData.proceso_proc} onChange={handleChange}>
                <option value="">Seleccione un proceso...</option>
                {procesos?.map((p) => (
                  <option key={p.proc} value={p.proc}>{p.AUX7}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col} md="6">
              <Form.Label>Rol en el Proceso</Form.Label>
              <Form.Select name="rol_id" value={formData.rol_id} onChange={handleChange} disabled={!formData.proceso_proc}>
                <option value="">Seleccione un rol...</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>{r.nombre}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Row>

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={() => navigate(-1)}>Cancelar</Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Guardando...' : 'Guardar Persona'}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default AddPersonaForm;